import { Controller } from "stimulus"
import iconv from "iconv-lite"
import { parseLocaleNumber, formatLocaleNumber } from "@react/helpers/helpers"

export default class extends Controller {
  static targets = ['title', 'encoding']

  // Totals span the whole filter, not the page on screen; the label says which.
  renderTotals(api) {
    const filteredRows = api.rows({ search: 'applied' }).count()
    const label        = filteredRows === api.rows().count() ?
      I18n.t('datatable.total', { rows: formatLocaleNumber(filteredRows, 0) }) :
      I18n.t('datatable.total_filtered', { rows: formatLocaleNumber(filteredRows, 0) })
    let labelPlaced = false

    api.columns().every(function () {
      const footer = this.footer()
      if (!footer) return

      if (!(this.type() || '').includes('num')) {
        footer.textContent = labelPlaced ? '' : label
        labelPlaced = true
        return
      }

      const total = api
        .column(this.index(), { search: 'applied' })
        .data()
        .reduce((sum, value) => {
          const number = parseLocaleNumber(value)
          return isNaN(number) ? sum : sum + number
        }, 0)

      footer.textContent = formatLocaleNumber(total)
    })
  }

  connect() {
    this._initializeTableHandler = this.initializeTable.bind(this)
    window.addEventListener("csv-ready", this._initializeTableHandler, { once: true })
  }
  
  initializeTable = () => {
    const waitForDataTables = () => {
      if (window.jQuery && typeof window.jQuery.fn.DataTable === 'function') {
        const $table = window.jQuery('#export_table')
        if ($table.length) {
          this.buildDataTable($table)
        } else {
          setTimeout(waitForDataTables, 50)
        }
      } else {
        setTimeout(waitForDataTables, 50)
      }
    }

    waitForDataTables()
  }

  buildDataTable($table) {
    const controller = this

    $table.DataTable({
      columnControl: ['order', ['search']],
      scrollX: true,
      fixedHeader: true,
      columnDefs: [{ targets: '_all', className: 'text-cut dt-left' }],
      ordering: { indicators: false, handler: true },
      dom: 'Bfrt<"d-flex j-c-between a-i-center mt-2"ip>',
      buttons: [
        {
          text: `<i class="fa fa-download"></i> ${I18n.t('export.download')}`,
          className: 'btn btn-primary small btn-sm mr-2',
          action: (e, dt, button, config) => {
            const exportData = dt.buttons.exportData({ columns: ':visible' })
            const csvRows = []

            const addCsvQuotes = (field) => {
              if (field == null) return '""'
              const str = String(field)
              const escaped = str.replace(/"/g, '""')
              return `"${escaped}"`
            }

            csvRows.push(exportData.header.map(addCsvQuotes).join(';'))
            exportData.body.forEach(row => {
              csvRows.push(row.map(addCsvQuotes).join(';'))
            })

            const csvString = csvRows.join('\r\n')
            const encoding = this.data.get('encoding') || 'utf-8'
            const encoded = iconv.encode(csvString, encoding)

            const blob = new Blob([encoded], { type: `text/csv;charset=${encoding};` })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${this.data.get('title') || 'export'}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
          }
        }
      ],
      deferRender: true,
      stateSave: true,
      pageLength: 50,
      language: {
        lengthMenu: I18n.t('datatable.length_menu'),
        zeroRecords: I18n.t('datatable.zero_records'),
        info: I18n.t('datatable.info'),
        infoEmpty: I18n.t('datatable.info_empty'),
        infoFiltered: I18n.t('datatable.info_filtered'),
        search: I18n.t('datatable.search'),
        decimal: I18n.t('number.format.separator'),
        thousands: I18n.t('number.format.delimiter'),
        paginate: {
          first: "«", last: "»", next: "›", previous: "‹"
        },
        columnControl: {
          dropdown: I18n.t('datatable.column_control.dropdown'),
          order: I18n.t('datatable.column_control.order'),
          search: {
            datetime: {
              empty: I18n.t('datatable.column_control.search.datetime.empty'),
              equal: I18n.t('datatable.column_control.search.datetime.equal'),
              greater: I18n.t('datatable.column_control.search.datetime.greater'),
              less: I18n.t('datatable.column_control.search.datetime.less'),
              notEmpty: I18n.t('datatable.column_control.search.datetime.not_empty'),
              notEqual: I18n.t('datatable.column_control.search.datetime.not_equal')
            },
            number: {
              empty: I18n.t('datatable.column_control.search.number.empty'),
              equal: I18n.t('datatable.column_control.search.number.equal'),
              greater: I18n.t('datatable.column_control.search.number.greater'),
              greaterOrEqual: I18n.t('datatable.column_control.search.number.greater_or_equal'),
              less: I18n.t('datatable.column_control.search.number.less'),
              lessOrEqual: I18n.t('datatable.column_control.search.number.less_or_equal'),
              notEmpty: I18n.t('datatable.column_control.search.number.not_empty'),
              notEqual: I18n.t('datatable.column_control.search.number.not_equal')
            },
            text: {
              contains: I18n.t('datatable.column_control.search.text.contains'),
              empty: I18n.t('datatable.column_control.search.text.empty'),
              ends: I18n.t('datatable.column_control.search.text.ends'),
              equal: I18n.t('datatable.column_control.search.text.equal'),
              notContains: I18n.t('datatable.column_control.search.text.not_contains'),
              notEmpty: I18n.t('datatable.column_control.search.text.not_empty'),
              notEqual: I18n.t('datatable.column_control.search.text.not_equal'),
              starts: I18n.t('datatable.column_control.search.text.starts')
            }
          }
        }
      },
      footerCallback: function () {
        controller.renderTotals(this.api())
      },
      initComplete: () => {
        window.dispatchEvent(new Event('datatable-ready'))
        
        const leftButtons = $('#left-buttons .dt-button')
        
        if (leftButtons.length) leftButtons.remove()
          
          
        const filterField = $('#datatable-filter .dt-search')
        if (filterField.length) filterField.remove()
          
        const dtButtons = $('.dt-buttons .dt-button').detach()
        const dtFilter = $('.dt-search').detach()
        
        $('#left-buttons').prepend(dtButtons)
        $('#datatable-filter').append(dtFilter)
        
        dtFilter.find('label').addClass('mb-0 d-flex a-i-center')
        dtFilter.find('input').addClass('form-control form-control-sm')
        
        $('.dt-column-header').each(function () {
          const $dtccs = $(this).children('.dtcc')
          if ($dtccs.length > 1) $dtccs.slice(1).remove()
          })
      }
    })

    const lengthLabel = $('#export_table_length label')
    if (lengthLabel.length) lengthLabel.addClass('d-flex a-i-center mb-4')

    const lengthSelect = $('#export_table_length select')
    if (lengthSelect.length) lengthSelect.addClass('form-control ml-2 mr-2')
  }
}
